import React, { useState, useEffect } from 'react';
import './Dashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://dev.api.propacity.in';

const Dashboard = () => {
  // Filter States
  const [selectedProject, setSelectedProject] = useState('620');
  const [fromDate, setFromDate] = useState('2026-04-01');
  const [toDate, setToDate] = useState('2026-06-30');
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  // Authentication Handoff Exchange State
  const [isExchangingToken, setIsExchangingToken] = useState(false);


  // API Data States
  const [headerStats, setHeaderStats] = useState({
    team_count: 0,
    active_projects: 0,
    visit_target: 0,
    visit_achieved: 0,
    booking_target: 0,
    bookings_achieved: 0,
  });
  const [performers, setPerformers] = useState([]);
  const [projectsList, setProjectsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Helper to build common request payload
  const buildPayload = () => {
    const payload = {
      Source: [21408],
    };

    // Project filter (convert 'All' to empty array or specific ID array)
    if (selectedProject && selectedProject !== 'All') {
      payload.Project = [parseInt(selectedProject, 10)];
    } else {
      payload.Project = [];
    }

    // Date range filter
    if (fromDate || toDate) {
      payload.created_date = {};
      if (fromDate) payload.created_date.From = fromDate;
      if (toDate) payload.created_date.To = toDate;
    }

    return payload;
  };

  // Fetch all dashboard data
  const fetchDashboardData = async () => {
    if (isExchangingToken) return;
    setLoading(true);
    setError(null);
    try {
      const payload = buildPayload();
      const token = localStorage.getItem("authToken") || "";

      // 1. Fetch Header Stats
      const headerRes = await fetch(`${API_BASE_URL}/api/v2/analytics/getPresaleHeaderStats`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token,
        },
        body: JSON.stringify(payload),
      });
      if (!headerRes.ok) throw new Error('Failed to fetch header stats');
      const headerData = await headerRes.json();

      // 2. Fetch Performers
      const performersRes = await fetch(`${API_BASE_URL}/api/v2/analytics/getPresaleIndividualTargetVsAchieved`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token,
        },
        body: JSON.stringify(payload),
      });
      if (!performersRes.ok) throw new Error('Failed to fetch performance data');
      const performersData = await performersRes.json();

      // Update State
      if (headerData.status === 200 && headerData.data) {
        setHeaderStats(headerData.data);
      }
      if (performersData.status === 200 && Array.isArray(performersData.data)) {
        setPerformers(performersData.data);

        // Dynamically extract unique projects for the filter list if not already loaded
        const projMap = new Map();
        performersData.data.forEach(user => {
          if (Array.isArray(user.projects)) {
            user.projects.forEach(p => {
              projMap.set(p.project_id, p.project_name);
            });
          }
        });
        const extractedProjects = Array.from(projMap.entries()).map(([id, name]) => ({
          project_id: id,
          project_name: name,
        }));
        if (extractedProjects.length > 0) {
          setProjectsList(extractedProjects);
        }
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Something went wrong while fetching data.');
    } finally {
      setLoading(false);
    }
  };

  // Fetch project list from project-wise API on mount to ensure we have all options
  const fetchProjectsListOnly = async (tokenOverride) => {
    try {
      const payload = buildPayload();
      const token = tokenOverride || localStorage.getItem("authToken") || "";
      const res = await fetch(`${API_BASE_URL}/api/v2/analytics/getPresaleProjectWiseTargetVsAchieved`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token,
        },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        const result = await res.json();
        if (result.status === 200 && Array.isArray(result.data)) {
          setProjectsList(result.data);
        }
      }
    } catch (err) {
      console.warn('Failed to fetch projects list from project API, will fallback to extracting from performers:', err);
    }
  };

  useEffect(() => {
    const handleInit = async () => {
      const hash = window.location.hash;
      const searchParams = new URLSearchParams(window.location.search);
      let codeVal = '';

      if (hash && hash.includes('code=')) {
        const match = hash.match(/code=([^&]+)/);
        if (match) {
          codeVal = match[1];
        }
      } else if (searchParams.has('code')) {
        codeVal = searchParams.get('code');
      }

      if (codeVal) {
        setIsExchangingToken(true);
        setError(null);
        // Clean up URL immediately so the code doesn't leak
        window.history.replaceState(null, '', window.location.pathname);

        try {
          const res = await fetch(`${API_BASE_URL}/api/v1/auth/exchangeHandoff`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ code: codeVal }),
          });

          if (!res.ok) {
            throw new Error('Failed to exchange handoff code for token.');
          }

          const result = await res.json();
          if (result.status === 200 && result.data && result.data.token) {
            const actualToken = result.data.token;
            localStorage.setItem('authToken', actualToken);
            setIsExchangingToken(false);
            
            // Fetch stats using newly saved token
            fetchProjectsListOnly(actualToken);
            
            // Explicitly call fetchDashboardData because state transitions happen asynchronously
            // and we want to ensure it fetches immediately with the valid token.
            setLoading(true);
            try {
              const payload = buildPayload();
              const headerRes = await fetch(`${API_BASE_URL}/api/v2/analytics/getPresaleHeaderStats`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': actualToken,
                },
                body: JSON.stringify(payload),
              });
              if (headerRes.ok) {
                const headerData = await headerRes.json();
                if (headerData.status === 200 && headerData.data) {
                  setHeaderStats(headerData.data);
                }
              }

              const performersRes = await fetch(`${API_BASE_URL}/api/v2/analytics/getPresaleIndividualTargetVsAchieved`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': actualToken,
                },
                body: JSON.stringify(payload),
              });
              if (performersRes.ok) {
                const performersData = await performersRes.json();
                if (performersData.status === 200 && Array.isArray(performersData.data)) {
                  setPerformers(performersData.data);
                }
              }
            } catch (err) {
              console.error(err);
            } finally {
              setLoading(false);
            }

          } else {
            throw new Error(result.message || 'Handoff exchange failed.');
          }
        } catch (err) {
          console.error(err);
          setError(err.message || 'Token exchange failed.');
          setIsExchangingToken(false);
        }
      } else {
        // No code in URL, load data normally using existing localStorage token
        fetchProjectsListOnly();
      }
    };

    handleInit();
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [selectedProject, fromDate, toDate]);


  // Compute stats card values
  const {
    team_count,
    active_projects,
    visit_target,
    visit_achieved,
    booking_target,
    bookings_achieved,
  } = headerStats;

  const visitHitPct = visit_target > 0 
    ? ((visit_achieved / visit_target) * 100).toFixed(1) + '%' 
    : (visit_achieved * 100).toFixed(1) + '%';

  const svToBookingPct = visit_achieved > 0 
    ? ((bookings_achieved / visit_achieved) * 100).toFixed(1) + '%' 
    : (bookings_achieved * 100).toFixed(1) + '%';

  const row1Stats = [
    { id: 1, title: 'Team Count', value: team_count, icon: '👥', bgColor: 'linear-gradient(135deg, #1e40af, #3b82f6)' },
    { id: 2, title: 'Active Projects', value: active_projects, icon: '🏗️', bgColor: 'linear-gradient(135deg, #065f46, #10b981)' },
    { id: 3, title: 'Visits Target', value: visit_target.toLocaleString(), icon: '🎯', bgColor: 'linear-gradient(135deg, #0f766e, #14b8a6)' },
    { id: 4, title: 'Visits Achieved', value: visit_achieved.toLocaleString(), icon: '👣', bgColor: 'linear-gradient(135deg, #0f766e, #0d9488)' },
  ];

  const row2Stats = [
    { id: 5, title: 'Visit Hit %', value: visitHitPct, icon: '📈', bgColor: 'linear-gradient(135deg, #c2410c, #f97316)' },
    { id: 6, title: 'Booking Target', value: booking_target, icon: '📋', bgColor: 'linear-gradient(135deg, #6b21a8, #8b5cf6)' },
    { id: 7, title: 'Bookings Achieved', value: bookings_achieved, icon: '✅', bgColor: 'linear-gradient(135deg, #6b21a8, #9333ea)' },
    { id: 8, title: 'SV ➔ Booking %', value: svToBookingPct, icon: '🏆', bgColor: 'linear-gradient(135deg, #991b1b, #ef4444)' },
  ];

  // Client side filtering (Search and Status)
  const filteredPerformers = performers.filter(p => {
    const matchesSearch = p.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.projects && p.projects.some(proj => proj.project_name.toLowerCase().includes(searchTerm.toLowerCase())));

    const statusStr = p.is_active ? 'Active' : 'Inactive';
    const matchesStatus = statusFilter === 'All' || statusStr === statusFilter;

    return matchesSearch && matchesStatus;
  });

  if (isExchangingToken) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', flexDirection: 'column', color: '#3b82f6', fontFamily: 'sans-serif' }}>
        <div style={{ fontSize: '1.5rem', fontWeight: '600', marginBottom: '16px' }}>🔄 Authenticating Session...</div>
        <div style={{ color: '#6b7280' }}>Exchanging secure handoff code for access token.</div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div>
          <h1>Pre-Sales Performance</h1>
          <p className="subtitle">FY 2026–27 | Formula-Driven Individual Performance Metrics</p>
        </div>
        <div className="current-date">
          <span>📊 Real-Time Operations</span>
        </div>
      </header>

      {error && (
        <div style={{ padding: '12px 20px', margin: '0 0 20px 0', borderRadius: '8px', backgroundColor: '#fee2e2', color: '#b91c1c', border: '1px solid #fca5a5' }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Row 1 Metrics */}
      <div className="kpi-grid">
        {row1Stats.map((stat) => (
          <div key={stat.id} className="kpi-card" style={{ background: stat.bgColor }}>
            <div className="kpi-card-header">
              <span className="kpi-title">{stat.title}</span>
              <span className="kpi-icon">{stat.icon}</span>
            </div>
            <div className="kpi-value">{loading ? '...' : stat.value}</div>
            <div className="kpi-footer">
              <span className="kpi-duration">Auto-calculated key metric</span>
            </div>
          </div>
        ))}
      </div>

      {/* Row 2 Metrics */}
      <div className="kpi-grid">
        {row2Stats.map((stat) => (
          <div key={stat.id} className="kpi-card" style={{ background: stat.bgColor }}>
            <div className="kpi-card-header">
              <span className="kpi-title">{stat.title}</span>
              <span className="kpi-icon">{stat.icon}</span>
            </div>
            <div className="kpi-value">{loading ? '...' : stat.value}</div>
            <div className="kpi-footer">
              <span className="kpi-duration">Target completion / conversion</span>
            </div>
          </div>
        ))}
      </div>

      {/* Table Section */}
      <div className="table-section">
        <div className="table-controls">
          <div className="table-title-area">
            <h2>Individual Performance Summary</h2>
            <span className="badge-pill">FY 2026-27</span>
          </div>
          <div className="controls-right">
            <input 
              type="text" 
              placeholder="Search Employee..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <div className="date-range-container" style={{ display: 'flex', gap: '8px' }}>
              <input 
                type="date" 
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                placeholder="From Date"
                className="date-input"
                title="From Date"
              />
              <input 
                type="date" 
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                placeholder="To Date"
                className="date-input"
                title="To Date"
              />
            </div>
            <select 
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="filter-select"
            >
              <option value="All">All Projects</option>
              {projectsList.map(p => (
                <option key={p.project_id} value={p.project_id}>
                  {p.project_name}
                </option>
              ))}
            </select>
            <select 
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="filter-select"
            >
              <option value="All">All Statuses</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>

        <div className="table-responsive">
          {loading && performers.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280', fontSize: '1.1rem' }}>
              🔄 Loading Performance Data...
            </div>
          ) : (
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>#</th>
                  <th>Employee</th>
                  <th>Projects</th>
                  <th>Status</th>
                  <th className="num-col">V.Target</th>
                  <th className="num-col">V.Achieved</th>
                  <th className="num-col">Visit%</th>
                  <th className="num-col">B.Target</th>
                  <th className="num-col">Bookings</th>
                  <th className="num-col">Booking %</th>
                  <th className="num-col">SV ➔ Bkg %</th>
                  <th style={{ textAlign: 'center' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredPerformers.length > 0 ? (
                  filteredPerformers.map((p, idx) => {
                    const statusStr = p.is_active ? 'Active' : 'Inactive';
                    
                    // Calculations
                    const visitPct = p.visit_target > 0 
                      ? ((p.visit_achieved / p.visit_target) * 100).toFixed(1) + '%' 
                      : (p.visit_achieved * 100).toFixed(1) + '%';
                    const bookingPct = p.booking_target > 0 
                      ? ((p.bookings_achieved / p.booking_target) * 100).toFixed(1) + '%' 
                      : (p.bookings_achieved * 100).toFixed(1) + '%';
                    const svBkgPct = p.visit_achieved > 0 
                      ? ((p.bookings_achieved / p.visit_achieved) * 100).toFixed(1) + '%' 
                      : (p.bookings_achieved * 100).toFixed(1) + '%';

                    // Performance Status Indicator
                    let statusIndicator = 'On Track';
                    if (!p.is_active) {
                      statusIndicator = 'Inactive';
                    } else if (p.visit_achieved < p.visit_target * 0.5) {
                      statusIndicator = 'Behind';
                    }

                    return (
                      <tr key={p.user_uuid || p.user_id}>
                        <td>{idx + 1}</td>
                        <td className="emp-name">{p.user_name}</td>
                        <td>
                          {p.projects && p.projects.length > 0 
                            ? p.projects.map(proj => proj.project_name).join(', ') 
                            : 'No Project'}
                        </td>
                        <td>
                          <span className={`status-tag ${statusStr.toLowerCase()}`}>
                            {statusStr}
                          </span>
                        </td>
                        <td className="num-col font-mono">{p.visit_target}</td>
                        <td className="num-col font-mono">{p.visit_achieved}</td>
                        <td className="num-col font-mono highlight-val">{visitPct}</td>
                        <td className="num-col font-mono">{p.booking_target}</td>
                        <td className="num-col font-mono">{p.bookings_achieved}</td>
                        <td className="num-col font-mono highlight-val">{bookingPct}</td>
                        <td className="num-col font-mono highlight-val">{svBkgPct}</td>
                        <td style={{ textAlign: 'center' }}>
                          <span className={`perf-indicator ${statusIndicator.toLowerCase().replace(' ', '-')}`}>
                            <span className="dot"></span>
                            {statusIndicator}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="12" className="no-data">No employee matching active filter.</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
